import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { MdSystemUpdate } from 'react-icons/md';
import { RiftboundButton } from './ui/button';
import { reloadToFreshApp } from '../utils/pwaReload';

/** Periodic `/sw.js` check while a tab stays open. Browser already fetches it on navigation. */
const SW_PROBE_INTERVAL_MS = 60 * 60 * 1000;

/** One extra check when returning to a background tab, at most once per interval. */
const VISIBILITY_PROBE_THROTTLE_MS = SW_PROBE_INTERVAL_MS;

/**
 * Shown when a new service worker is waiting to activate.
 * Lets users choose when to reload rather than auto-reloading mid-session.
 */
export function PwaUpdateBanner() {
  const periodicRef = useRef<number | undefined>(undefined);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const swScriptUrlRef = useRef('');
  const lastVisibilityProbeRef = useRef(0);
  const [reloading, setReloading] = useState(false);

  function schedulePeriodicProbes(swUrl: string, registration: ServiceWorkerRegistration): void {
    if (periodicRef.current != null) {
      window.clearInterval(periodicRef.current);
    }

    async function probeForUpdate(): Promise<void> {
      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) return;
      const reg = registrationRef.current;
      if (!reg) return;
      try {
        await reg.update();
      } catch {
        /* ignore stale registration / aborted */
      }
    }

    registrationRef.current = registration;
    swScriptUrlRef.current = swUrl;
    periodicRef.current = window.setInterval(() => void probeForUpdate(), SW_PROBE_INTERVAL_MS);
  }

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    // Default plugin reload races `event.isUpdate` and can no-op. The Reload
    // click below always goes through reloadToFreshApp instead.
    onNeedReload() {},
    onRegisteredSW(swUrl, registration) {
      if (!registration || !swUrl) {
        registrationRef.current = registration ?? null;
        swScriptUrlRef.current = '';
        return;
      }
      schedulePeriodicProbes(swUrl, registration);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.warn('PWA SW registration failed', error);
    },
  });

  useEffect(
    () => () => {
      if (periodicRef.current != null) window.clearInterval(periodicRef.current);
      periodicRef.current = undefined;
      registrationRef.current = null;
    },
    [],
  );

  useEffect(() => {
    function onForeground(): void {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityProbeRef.current < VISIBILITY_PROBE_THROTTLE_MS) return;
      lastVisibilityProbeRef.current = now;

      const reg = registrationRef.current;
      const url = swScriptUrlRef.current;
      if (
        typeof navigator !== 'undefined' &&
        'onLine' in navigator &&
        !navigator.onLine
      ) return;
      if (!reg || !url) return;

      void reg.update().catch(() => undefined);
    }

    document.addEventListener('visibilitychange', onForeground);
    return () => document.removeEventListener('visibilitychange', onForeground);
  }, []);

  if (!needRefresh) return null;

  return (
    <Box
      position="fixed"
      bottom={{ base: 3, md: 4 }}
      right={{ base: 3, md: 4 }}
      left={{ base: 3, md: 'auto' }}
      maxW={{ md: '340px' }}
      zIndex={2000}
      bg="bgElevated"
      borderWidth="hairline"
      borderColor="border"
      borderRadius="xl"
      boxShadow="0 4px 24px rgba(0,0,0,0.5)"
      overflow="hidden"
      style={{ backdropFilter: 'blur(16px)' }}
    >
      <Box h="2px" bg="accent" />
      <Flex align="center" gap={3} px={3} py={3}>
        <Box color="accent" flexShrink={0}>
          <MdSystemUpdate size={20} />
        </Box>
        <Text fontSize="sm" flex={1} color="text">
          New version available
        </Text>
        <HStack gap={2} flexShrink={0}>
          <RiftboundButton
            treatment="primary"
            size="xs"
            loading={reloading}
            onClick={() => {
              setReloading(true);
              // Plugin posts SKIP_WAITING; reloadToFreshApp waits for the new
              // SW to claim this tab (or times out) then reloads. A bare
              // location.reload() here is still served by the old SW.
              void updateServiceWorker();
              void reloadToFreshApp('update');
            }}
          >
            Reload
          </RiftboundButton>
        </HStack>
      </Flex>
    </Box>
  );
}
